import { computed, ref } from 'vue'
import { defineStore } from 'pinia';
import { useI18n } from "vue-i18n";
import { useModalStore } from '@/stores/modal.js';
import { useAuthenticationStore } from '@/stores/authentication.js';
import { useToastStore } from '@/stores/toast.js';
import { indexMakes } from '@/lib/api/makes.js';
import { createUserGarage, indexUserGarages, updateUserGarage } from '@/lib/api/user_garages.js'
import { createApi } from '@/lib/api.service.js'
import { useDebounceFn } from '@vueuse/core'

import {
  indexUserVehicles,
  createUserVehicle,
  deleteUserVehicle,
  updateUserVehicle,
  uploadImage
} from '@/lib/api/user_vehicles.js'
import { indexCars } from '@/lib/api/cars.js'
import { indexUserQuotes } from '@/lib/api/user_quotes';
import { indexUserVehicleServiceHistories } from '@/lib/api/user_vehicle_service_histories';

export const useGarageStore = defineStore('garage', () => {
  // I18n
  const { t } = useI18n();

  // Stores
  const modalStore = useModalStore();
  const authenticationStore = useAuthenticationStore();

  // Variables
  const userGarage = ref(null);
  const userVehicles = ref([]);
  const currentVehicle = ref(null);
  const yearRange = ref([]);
  const makes = ref([]);
  const models = ref();
  const trims = ref(["North", "Competition"]);
  const emptyVehicle = ref({
    id: null,
    year: t('modal.add_vehicle.year_placeholder'),
    make_id: t('modal.add_vehicle.make_placeholder'),
    vehicle_model_id: t('modal.add_vehicle.model_placeholder'),
    vehicle_edition_id: t('modal.add_vehicle.trim_placeholder'),
    user_id: null,
    user_garage_id: null,
    mileage: null,
    nickname: null,
    description: null,
    modifications: null,
  })
  const quoteStatuses = ref(['In Progress', 'Expired', 'Active'])
  const allQuotes = ref([]);
  const allServiceHistories = ref([]);
  const currentVehicleRecommendedServices = ref([]);
  const isMovingToServiceHistory = ref(false);

  const currentVehicleQuotes = computed(() => allQuotes.value.filter(x => x.user_vehicle_id === currentVehicle.value?.id && x.user_vehicle_service_histories.length == 0))
  const otherQuotes = computed(() => allQuotes.value.filter(x => x.user_vehicle_id !== currentVehicle.value?.id))
  const currentVehicleServiceHistory = computed(() => allServiceHistories.value)

  const $reset = () => {
    userVehicles.value = [];
    userVehicles.value.push(emptyVehicle.value);
    currentVehicle.value = null;
    userGarage.value = null;
  }

  const getMyGarage = async () => {
    userVehicles.value = [];
    userGarage.value = null;

    let userGarageParams = new URLSearchParams();
    userGarageParams.set('query[user_id_eq]', authenticationStore.currentUser.authenticatable_id);
    userGarageParams.set('query[archived_eq]', 'false');

    await indexUserGarages(userGarageParams)
      .then(async (userGarages) => {
        if (userGarages && userGarages.length > 0) {
          return userGarages[0];
        } else {
          let data = {};
          data.user_id = authenticationStore.currentUser.authenticatable_id;
          return await createUserGarage(data)
        }
      })
      .then((ug) => {
        userGarage.value = ug;

        let userVehicleParams = new URLSearchParams();
        userVehicleParams.set('query[s]', 'created_at DESC')
        userVehicleParams.set('query[user_id_eq]', authenticationStore.currentUser.authenticatable_id);
        userVehicleParams.set('query[garage_id_eq]', userGarage.value.id);
        userVehicleParams.set('query[archived_eq]', 'false');

        return indexUserVehicles(userVehicleParams)
      })
      .then((vehicles) => {
        userVehicles.value = vehicles;

        if (userVehicles.value.length === 0) {
          userVehicles.value.push(emptyVehicle.value);
        }

        // Default to first vehicle if one is not already set
        let vehicle = loadCurrentVehicle();
        if (!vehicle) {
          setCurrentVehicle(userVehicles.value[0]);
        } else {
          // Current vehicle may have been updated, find vehicle and 'refresh' the instance
          let refreshVehicle = userVehicles.value.find(refreshVehicle => refreshVehicle.id === vehicle.id)
          if (refreshVehicle) {
            setCurrentVehicle(refreshVehicle);
          }
        }
      })
  };

  const getUserQuotes = async () => {
    let userQuoteParams = new URLSearchParams();
    userQuoteParams.set('query[s]', 'created_at DESC');
    userQuoteParams.set('query[user_id_eq]', authenticationStore.currentUser.authenticatable_id);
    userQuoteParams.set('query[archived_eq]', 'false');

    await indexUserQuotes(userQuoteParams).then( async (userQuotes) => {
      allQuotes.value = userQuotes || [];
    })
  }

  const getUserServiceHistories = async () => {
    let modelParams = new URLSearchParams();
    modelParams.set('query[s]', 'created_at DESC');
    modelParams.set('query[user_quote_user_id_eq]', authenticationStore.currentUser.authenticatable_id);

    await indexUserVehicleServiceHistories(modelParams).then( async (serviceData) => {
      allServiceHistories.value = serviceData;
    })
  }

  const moveServiceToServiceHistory = async (service) => {
    isMovingToServiceHistory.value = true;
    const service_history = {
      user_quote_id: service.value.id,
      user_vehicle_id: service.value.user_vehicle_id,
      completion_date: new Date().toISOString() // Explicitly set completion date for manually moved services
    };

    try {
      const response = await createApi(`${import.meta.env.VITE_BACKEND_URL}/api/v2/servicechek/user_vehicle_service_histories`, service_history);

      if (response.status >= 200 && response.status < 300) {

        const index = allQuotes.value.findIndex(item => item.id === service.value.id);

        if (index !== -1) {
          const updatedItem = allQuotes.value[index]["user_vehicle_service_histories"].push(response.json);

          allQuotes.value[index] = updatedItem;

        } else {
          console.warn(`Quote with ID ${service.value.id} not found.`);
        }

        allServiceHistories.value.push(response.json);
        
        // Refresh the data to ensure consistency
        await getUserQuotes();
        await getUserServiceHistories();
      } else {
        throw new Error(response.json.error);
      }
    } catch (e) {
      console.error(e);
      throw e;
    } finally {
      isMovingToServiceHistory.value = false;
    }
  }

  const getYearRange = async () => {
    let years = [];

    for (let year = 1980; year <= new Date().getFullYear(); year++) {
      years.push(year);
    }

    return years.reverse();
  }

  const getMakes = async () => {
    let makeParams = new URLSearchParams();
    makeParams.set('query[s]', 'name asc');
    makeParams.set('query[archived_eq]', false);
    makeParams.set('query[parity_verified_eq', true);

    await indexMakes(makeParams)
      .then((makesData) => {
        makes.value = makesData;
        return makes.value;
      });
  }

  const getModels = async (year, makeId) => {
    let modelParams = new URLSearchParams();
    modelParams.set('query[s]', 'vehicle_name asc');
    modelParams.set('query[vehicle_year_eq]', year);
    modelParams.set('query[make_id_eq]', makeId);
    modelParams.set('query[archived_eq]', false);
    modelParams.set('query[parity_verified_eq', true);

    await indexCars(modelParams)
      .then((modelsData) => {
        modelsData = uniqueCars(modelsData, 'vehicle_name');
        models.value = modelsData;
        return modelsData.value;
      });
  }

  const getTrims = async (year, makeId, modelId) => {
    let modelParams = new URLSearchParams();
    modelParams.set('query[s]', 'vehicle_edition_name asc');
    modelParams.set('query[vehicle_year_eq]', year);
    modelParams.set('query[make_id_eq]', makeId);
    modelParams.set('query[vehicle_model_id_eq]', modelId);
    modelParams.set('query[archived_eq]', false);
    modelParams.set('query[parity_verified_eq', true);

    await indexCars(modelParams)
      .then((trimsData) => {
        trimsData = uniqueCars(trimsData, 'vehicle_edition_name');
        trims.value = trimsData.sort((a, b) => a.vehicle_edition_name.localeCompare(b.vehicle_edition_name));
        return trims.value;
      });
  }
  
  const getCurrentVehicleServiceHistory = async (current_vehicle_id) => {
    let modelParams = new URLSearchParams();

    let returnServiceHistory = []
    await indexUserVehicleServiceHistories(modelParams)
      .then((serviceData) => {
        
        returnServiceHistory = serviceData;
      });

    return indexUserQuotes;
  }

  const getCurrentVehicleQuotes = async (current_vehicle_id) => {
    let modelParams = new URLSearchParams();
    modelParams.set('query[s]', 'id desc');
    modelParams.set('query[user_vehicle_id_eq]', current_vehicle_id);
    modelParams.set('query[user_id_eq]', authenticationStore.currentUser.authenticatable_id);
    // modelParams.set('query[make_id_eq]', makeId);
    // modelParams.set('query[vehicle_model_id_eq]', modelId);
    // modelParams.set('query[archived_eq]', false);

    let returnQuotes = []
    await indexUserQuotes(modelParams)
      .then((quotesData) => {


        quotesData.forEach(obj => {
          var date = new Date(obj.created_at);
          // obj.title = 'Oil Change & Tire Rotation';
          obj.description = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Cras non eleifend dui. Ut consequat risus et interdum sodales. Vivamus ut mi vel ligula venenatis lobortis. Cras et diam eget massa pellentesque posuere. Donec tempus erat in magna egestas, et lobortis urna bibendum. Praesent accumsan dolor est, sit amet tempus lacus viverra in. Aliquam ac accumsan quam.';
          obj.expiry = new Date(date.setMonth(date.getMonth() + 1));
          obj.status = "Active"
        });
        

        returnQuotes = quotesData;
      });

    return returnQuotes;
  }

  const uniqueCars = (cars, attribute) => {
    const seenCars = new Set();

    return cars.filter(car => {
      if (seenCars.has(car[`${attribute}`])) {
        return false;
      } else {
        seenCars.add(car[`${attribute}`]);
        return true;
      }
    });
  }

  const setCurrentVehicle = (vehicle) => {
    const toastStore = useToastStore();
    const previousVehicle = currentVehicle.value;
    
    if(vehicle && !vehicle.id) {
      vehicle = null;
    }

    currentVehicle.value = vehicle;
    
    // Show toast notification when vehicle changes (but not on initial load)
    if (previousVehicle && vehicle && previousVehicle.id !== vehicle.id) {
      const vehicleName = vehicle.full_vehicle_name || t('my_garage.unassigned_vehicle');
      toastStore.success(`Current vehicle changed to ${vehicleName}`);
    }
  }

  const loadCurrentVehicle = () => {
    return currentVehicle.value;
  };

  const createVehicle = useDebounceFn(async (vehicle) => {
    delete vehicle.id;

    // Find car/vehicle record from trims
    let car = trims.value.filter((car) => {
      return car.vehicle_year === vehicle.year
        && car.make_id === vehicle.make_id
        && car.vehicle_model_id === vehicle.vehicle_model_id
        && car.vehicle_edition_id === vehicle.vehicle_edition_id
    })

    if (car && car.length > 0) {
      car = car[0];
      vehicle.car_id = car.id;
      vehicle.vehicle_id = car.vehicle_id;
    }

    await createUserVehicle(vehicle)
      .then(async (data) => {
        modalStore.resetModal();
        setCurrentVehicle(data);
        await getMyGarage();
      })
  }, 500, { maxWait: 5000 })

  const updateVehicle = async (vehicle) => {
    // Find car/vehicle record from trims
    // Something in here isn't correct, trims car isn't showing up for some reason
    let car = trims.value.filter((car) => { return car.vehicle_year == vehicle.year && car.make_id == vehicle.make_id && car.vehicle_model_id == vehicle.vehicle_model_id && car.vehicle_edition_id == vehicle.vehicle_edition_id })
    if (car && car.length > 0) {
      car = car[0];
      vehicle.car_id = car.id;
      vehicle.vehicle_id = car.vehicle_id;
    }

    await updateUserVehicle(vehicle.id, vehicle)
      .then(async () => {
        await getMyGarage();
        modalStore.resetModal();
      })
  }

  const updateVehicleImage = async(image) => {
    await uploadImage(currentVehicle.value.id, image)
      .then(async () => {
        await getMyGarage();
      });
  }

  const removeVehicle = async (vehicle) => {
    await deleteUserVehicle(vehicle.id)
      .then(async () => {
        await getMyGarage();
        setCurrentVehicle(userVehicles.value[0] || emptyVehicle.value);
      });
  }

  const archiveVehicle = async (vehicle) => {
    const updatedVehicle = { ...vehicle, archived: true };
    await updateUserVehicle(vehicle.id, updatedVehicle)
      .then(async () => {
        await getMyGarage();
        if (currentVehicle.value?.id === vehicle.id) {
          setCurrentVehicle(userVehicles.value[0] || emptyVehicle.value);
        }
      });
  }

  const updateGarage = async (garage) => {
    await updateUserGarage(garage.id, garage)
      .then(async () => {
        await getMyGarage();
        modalStore.resetModal();
      })
  };

  const maxVehicles = () => {
    if (userGarage.value) {
      return userVehicles.value.length >= userGarage.value.maximum_vehicles;
    } else {
      return userVehicles.value.length >= 3;
    }
  }

  const getVehicleDropdowns = async () => {
    await getMakes()
      .then(async () => {
        return await getModels(modalStore.newVehicle.year, modalStore.newVehicle.make_id);
      })
      .then(async() => {
        return await getTrims(modalStore.newVehicle.year, modalStore.newVehicle.make_id, modalStore.newVehicle.vehicle_model_id);
      });
  }

  return {
    userGarage,
    currentVehicle,
    userVehicles,
    yearRange,
    makes,
    models,
    trims,
    emptyVehicle,
    allQuotes,
    currentVehicleQuotes,
    otherQuotes,
    currentVehicleRecommendedServices,
    currentVehicleServiceHistory,
    allServiceHistories,
    isMovingToServiceHistory,
    moveServiceToServiceHistory,
    getYearRange,
    getMakes,
    getModels,
    getTrims,
    // getCurrentVehicleQuotes,
    // getCurrentVehicleServiceHistory,
    setCurrentVehicle,
    getMyGarage,
    getUserQuotes,
    getUserServiceHistories,
    createVehicle,
    updateVehicle,
    removeVehicle,
    archiveVehicle,
    updateGarage,
    maxVehicles,
    getVehicleDropdowns,
    updateVehicleImage,
    $reset
  };
}, {
  persist: {
    key: 'garage-store',
    paths: ['currentVehicle']
  }
});
